import React from 'react';

import BusComponent from '../BusComponent';
import { injectIntl } from 'react-intl';
import UploadPointDataPanel from './UploadPointDataPanel';
import MapLayersPanelHeader from './MapLayersPanelHeader';
import BasemapEditor from './basemapEditor/BasemapEditor';
import GeoJsonLibraryLegend from '../geoJsonLibrary/legend/GeoJsonLibraryLegend';
import LayerLibraryLegend from '../layerLibrary/legend/LayerLibraryLegend';
import LatLongAndGeocode from '../userDataUploadEditor/LatLongAndGeocode';
import UserDataLayerEditor from '../userDataUploadEditor/UserDataLayerEditor';
import { HelpTourTargets } from '../../enums/HelpTourDefinitions';
import DealListSelector from './dealListSelector/DealListSelector';
import DealsFromSelectedListPanel from './dealListSelector/DealsFromSelectedListPanel';

export const ActiveView = {
    INITIAL_VIEW: 0,
    DATA_UPLOAD: 1,
    SETTINGS: 2,
    ANNOTATIONS: 3,
    USER_LAYER_EDIT: 4,
    GEO_CODE: 5,
    DEALS_FROM_DEAL_LIST: 6,
};

class MapLayersPanel extends BusComponent {
    constructor(props, context) {
        super(props, context);

        this.state = {
            activeView: ActiveView.INITIAL_VIEW,
            isDataUploadVisible: false,
            isGeoCodePanelVisible: false,
            isUserDataLayerEditorVisible: false,
            geoJsonLegendMetadata: undefined,
            userDataLayers: [],
            libraryGroupsInfo: undefined,
        };

        this.bindGluBusEvents({
            EDIT_USER_LAYER_REQUEST: this.onEditUserLayerRequest,
            USER_DATA_UPLOAD_PARSED: this.onDataUploadParsed,
            USER_DATA_ERROR: this.onUserDataError,
            USER_DATA_UPLOAD_FINISHED: this.onUserDataUploadFinished,
            USER_LAYER_DELETE_REQUEST: this.onDeleteUserLayer,
            OPEN_DEAL_LIST_PANEL: this.onOpenDealListPanel,
        });
    }

    componentDidMount() {
        this.bindGluBusEvents({
            ADD_LAYER_LIBRARY_GROUP: this.getLayerLibraryMetadata,
            REMOVE_LAYER_LIBRARY_GROUP: this.getLayerLibraryMetadata,
            TOGGLE_USER_DATA_UPLOAD_LEGEND_VISIBILITY: this.getLayerLibraryMetadata,
            UPDATE_USER_LAYER_VISIBILITY: this.getLayerLibraryMetadata,
        });

        this.getVariableMetadata();
        this.getLayerLibraryMetadata();
        this.getGeoJsonMetadata();
        this.getUserLayers();
        this.emit('MAP_CLEAR_ALL_HIGHLIGHT_RULES');
        this.emit('HELP_TOUR_STEP_LOADED');
    }

    componentWillUnmount() {
        this.unbindGluBusEvents();
    }

    onOpenDealListPanel = () => {
        this.setState({
            activeView: ActiveView.DEALS_FROM_DEAL_LIST,
        });
    };

    onUserDataError(payload) {
        const { type, userMessage: error } = payload;
        switch (type) {
            case 'UPLOAD_FILE_ERROR':
                this.setState({ activeView: ActiveView.DATA_UPLOAD, error: error });
                break;
            default:
                break;
        }
    }

    onClearError = () => {
        this.setState({ error: undefined });
    };

    onEditUserLayerRequest(userDataLayer) {
        this.setState({
            activeView: ActiveView.USER_LAYER_EDIT,
            userDataLayerId: userDataLayer.id,
        });
    }

    onUserDataUploadFinished = () => {
        this.setState({ activeView: ActiveView.USER_LAYER_EDIT });
    };

    onDataUploadParsed({ userUpload }) {
        this.setState({ userUpload, activeView: ActiveView.GEO_CODE, error: undefined });
    }

    onSetInitialView = () => {
        this.setState({ activeView: ActiveView.INITIAL_VIEW });
    };

    onAddLayersClick = () => {
        const { mapInstanceId, mapInstance } = this.props;

        this.emit('ADD_LAYER_LIBRARY_LAYERS_POPUP_REQUEST', {
            mapInstanceId,
            mapInstance,
        });
    };

    toggleDataUploadVisible = () => {
        this.setState({ activeView: ActiveView.INITIAL_VIEW });
    };

    toggleGeoCodePanelVisible = () => {
        this.setState({ activeView: ActiveView.DATA_UPLOAD });
    };

    onGeoCodePanelBack = () => {
        this.setState({
            activeView: ActiveView.DATA_UPLOAD,
            error: undefined,
            selection: undefined,
        });
    };

    onDataUploadClick = () => {
        this.toggleGeoCodePanelVisible();
    };

    onGeoJsonMetadataRetrieved = (geoJsonMetadata, target) => {
        if (target === this && geoJsonMetadata) {
            const { mapProperties } = geoJsonMetadata;
            this.setState({
                geoJsonLegendMetadata: mapProperties.legend,
            });
        }
    };

    onLayerLibraryDataRetrieved = (layerLibraryData, target) => {
        if (target === this) {
            const layerLibraryDataCopy = JSON.parse(JSON.stringify(layerLibraryData));
            this.setState({
                layerLibraryMetadata: layerLibraryDataCopy.groups,
            });
        }
    };

    onUserDataLayerReady = userDataLayer => {
        this.setState(prev => ({
            userDataLayers: [
                ...prev.userDataLayers.filter(layer => layer.id !== userDataLayer.id),
                userDataLayer,
            ],
        }));
    };

    render() {
        const { onClose, currentUser, mapInstance, onShowDataBrowser } = this.props;
        const {
            geoJsonLegendMetadata,
            libraryGroupsInfo,
            userDataLayers,
            userDataLayerId,
            userUpload,
            activeView,
            selection,
            error,
        } = this.state;

        const hasLayerLibraryLegend = libraryGroupsInfo ? libraryGroupsInfo.length > 0 : false;

        let panelContent;
        switch (activeView) {
            case ActiveView.DATA_UPLOAD:
                panelContent = (
                    <UploadPointDataPanel
                        error={error}
                        onClearError={this.onClearError}
                        onClose={this.toggleDataUploadVisible}
                        mapInstanceId={mapInstance.id}
                    />
                );
                break;
            case ActiveView.USER_LAYER_EDIT:
                panelContent = (
                    <UserDataLayerEditor
                        mapInstance={mapInstance}
                        userDataLayerId={userDataLayerId}
                        onDone={this.onSetInitialView}
                        onUserDataLayerReady={this.onUserDataLayerReady}
                    />
                );
                break;
            case ActiveView.GEO_CODE:
                panelContent = (
                    <LatLongAndGeocode
                        error={error}
                        selection={selection}
                        userUpload={userUpload}
                        mapInstanceId={mapInstance.id}
                        onBack={this.onGeoCodePanelBack}
                        onClose={this.toggleGeoCodePanelVisible}
                    />
                );
                break;
            case ActiveView.DEALS_FROM_DEAL_LIST:
                panelContent = (
                    <DealsFromSelectedListPanel
                        mapInstance={mapInstance}
                        onBack={this.onSetInitialView}
                        onClose={onClose}
                    />
                );
                break;

            default:
                break;
        }

        const isUserStarterTier = currentUser && currentUser.isStarterTierUser;

        return (
            <div
                className="map-layers-panel__wrapper"
                data-tourId={HelpTourTargets.MAP_LAYERS_PANEL}
            >
                <div className="map-layers-panel__popup">
                    {activeView === ActiveView.INITIAL_VIEW ? (
                        <div>
                            <MapLayersPanelHeader
                                onClose={onClose}
                                onDataUploadClick={this.onDataUploadClick}
                                onAddLayersClick={this.onAddLayersClick}
                                isUserStarterTier={isUserStarterTier}
                            />

                            <div className="map-layers-panel__popup__horizontal-divider" />

                            <div className="map-layers-panel__popup__scrollable-content">
                                <BasemapEditor
                                    onShowDataBrowser={onShowDataBrowser}
                                    mapInstance={mapInstance}
                                />

                                <DealListSelector mapInstance={mapInstance} />

                                {geoJsonLegendMetadata && (
                                    <GeoJsonLibraryLegend
                                        geoJsonLegendMetadata={geoJsonLegendMetadata}
                                        mapInstanceId={mapInstance.id}
                                        mapInstance={mapInstance}
                                    />
                                )}
                                {hasLayerLibraryLegend && (
                                    <LayerLibraryLegend
                                        className="layer-library-legend--simplified"
                                        libraryGroups={libraryGroupsInfo}
                                        mapInstanceId={mapInstance.id}
                                        userLayers={userDataLayers}
                                    />
                                )}
                            </div>
                        </div>
                    ) : (
                        panelContent
                    )}
                </div>
            </div>
        );
    }

    getGeoJsonMetadata = () => {
        this.bus.once('GEO_JSON_METADATA', this.onGeoJsonMetadataRetrieved);
        this.emit('GEO_JSON_METADATA_REQUEST', { source: this });
    };

    getLayerLibraryMetadata = () => {
        this.bus.once('LIBRARY_DATA', this.onLayerLibraryDataRetrieved);
        this.emit('LIBRARY_DATA_REQUEST', { source: this });
        const { mapInstance } = this.props;
        const { libraryDataLayers } = mapInstance;

        if (libraryDataLayers) {
            const groups = libraryDataLayers.map(layersGroup => {
                const groupInfo = libraryDataLayers.find(
                    groupMetadata => groupMetadata.id === layersGroup.id,
                );
                groupInfo.layers = layersGroup.layers;
                return groupInfo;
            });
            this.setState({
                libraryGroupsInfo: groups,
            });
        }
    };

    getVariableMetadata = () => {
        const { mapInstance } = this.props;

        this.emit('VARIABLE_SELECTION_METADATA_INFO_REQUEST', {
            source: this,
            mapInstanceId: mapInstance.id,
            dataDictionaryInfo: true,
            variableSelectionItems: mapInstance.dataTheme.variableSelection.items,
        });
    };

    getUserLayers = () => {
        const { mapInstance } = this.props;
        this.setState({
            userDataLayers: mapInstance.userDataLayers,
        });
    };

    onDeleteUserLayer = e => {
        const { mapInstance } = this.props;

        const userLayer = mapInstance.userDataLayers.find(
            layer => layer.title === e.userLayerTitle,
        );

        if (userLayer) {
            this.emit('DELETE_USER_LAYER', {
                userDataLayerId: userLayer.id,
                mapInstanceId: mapInstance.id,
                userData: e.userData,
            });
        }

        this.getUserLayers();
    };
}

export default injectIntl(MapLayersPanel);
